/**
 * ============================================================================
 * 🎯 GANTT TRANSFORM - Transformação de Grafo VSM para Timeline Horizontal
 * ============================================================================
 * 
 * Converte a estrutura de cards e conexões do mapa de processos em dados
 * estruturados para visualização em Gráfico de Gantt / Timeline.
 * 
 * Algoritmo: BFS com Ordenação Topológica (Kahn's Algorithm)
 * Complexidade: O(V + E) onde V = cards, E = conexões
 * 
 * @author Workflow Team
 * @version 1.0.0
 */

import { CardData, Connection } from '../types';
import { convertLtToMinutes } from './calculations';

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Representa uma tarefa no Gráfico de Gantt
 */
export interface GanttTask {
  /** ID único do card */
  id: string;
  /** Nome da atividade */
  name: string;
  /** Recurso responsável (Quem/Cargo) */
  resource: string;
  /** Minuto de início (baseado em predecessores) */
  startMinutes: number;
  /** Duração do trabalho efetivo (TRA) em minutos */
  workDuration: number;
  /** Duração de espera (TP - TRA) em minutos */
  waitDuration: number;
  /** Duração total (TP) em minutos */
  totalDuration: number;
  /** Minuto de término */
  endMinutes: number;
  /** IDs dos cards predecessores */
  dependencies: string[];
  /** Tipo do card para estilização */
  type: 'activity' | 'decision' | 'start' | 'end';
  /** Número sequencial para ordenação visual */
  sequence: number;
  /** Percentual de probabilidade de execução (para decisões) */
  probability: number;
  /** Flag para indicar se é uma barra de gap/espera entre tarefas */
  isGap?: boolean;
}

/**
 * Resultado da transformação com metadados
 */
export interface GanttTransformResult {
  /** Lista de tarefas para o Gantt */
  tasks: GanttTask[];
  /** Duração total do processo (caminho crítico) */
  totalDuration: number;
  /** Mapa de recursos para agrupamento */
  resourceGroups: Map<string, GanttTask[]>;
  /** IDs do caminho crítico */
  criticalPath: string[];
  /** Estatísticas do processo */
  stats: {
    totalWorkTime: number;
    totalWaitTime: number;
    efficiency: number;
    resourceCount: number;
  };
  /** Configuração de zoom dinâmico */
  zoom: {
    /** Escala sugerida: minutos por 100px */
    scale: number;
    /** Intervalo de marcas de tempo (minutos) */
    tickInterval: number;
    /** Label do intervalo (ex: '15min', '1h', '1d') */
    tickLabel: string;
  };
}

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Converte Lead Time (TP) para minutos considerando a unidade
 * 
 * @param value - Valor numérico do Lead Time
 * @param unit - Unidade de medida ('dias' | 'horas' | 'minutos' | 'segundos')
 * @returns Valor convertido para minutos
 */
function convertToMinutes(value: number, unit?: string): number {
  if (!value || isNaN(value)) return 0;
  
  switch (unit) {
    case 'dias':
      return value * 480; // 8 horas úteis por dia
    case 'horas':
      return value * 60;
    case 'segundos':
      return value / 60;
    case 'minutos':
    default:
      return value;
  }
}

/**
 * Converte entradas numéricas tolerando strings com sufixo (ex: "5min", "45,5")
 */
function parseNumericValue(value: unknown): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    if (!normalized) return 0;
    const direct = Number(normalized);
    if (Number.isFinite(direct)) return direct;
    const match = normalized.match(/-?\d+(\.\d+)?/);
    if (!match) return 0;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

/**
 * Extrai o recurso responsável do card
 * Prioridade: quem > responsavel > resources[0] > 'Não Atribuído'
 */
function extractResource(card: CardData): string {
  // Priorizar campo 'quem' para mapeamento de linha Y
  if (card.quem && card.quem.trim()) {
    return card.quem.trim();
  }
  if (card.responsavel && card.responsavel.trim()) {
    return card.responsavel.trim();
  }
  if (card.resources && card.resources.length > 0) {
    return card.resources[0].roleId || 'Não Atribuído';
  }
  return 'Não Atribuído';
}

/**
 * Mapeia tipo de card para tipo de Gantt
 */
function mapCardType(tipo: string): GanttTask['type'] {
  switch (tipo) {
    case 'card_inicial': return 'start';
    case 'card_final': return 'end';
    case 'decisao': return 'decision';
    default: return 'activity';
  }
}

// ============================================================================
// FUNÇÃO PRINCIPAL
// ============================================================================

/**
 * Transforma o grafo de cards VSM em estrutura de dados para Gráfico de Gantt
 * 
 * Utiliza BFS com ordenação topológica (Algoritmo de Kahn) para:
 * 1. Processar cards na ordem correta de dependências
 * 2. Calcular startTime baseado no maior endTime dos predecessores
 * 3. Identificar o caminho crítico automaticamente
 * 
 * @param cards - Array de cards do mapa
 * @returns Resultado estruturado para visualização Gantt
 */
export function transformToGantt(cards: CardData[]): GanttTransformResult {
  // Filtrar apenas cards ativos
  const activeCards = cards.filter(c => c.status !== 'removido');
  
  if (activeCards.length === 0) {
    return {
      tasks: [],
      totalDuration: 0,
      resourceGroups: new Map(),
      criticalPath: [],
      stats: { totalWorkTime: 0, totalWaitTime: 0, efficiency: 0, resourceCount: 0 },
      zoom: { scale: 5, tickInterval: 5, tickLabel: '5min' }
    };
  }

  // =========================================================================
  // FASE 1: Construir estruturas de adjacência
  // =========================================================================
  
  /** Mapa de ID para Card para acesso O(1) */
  const cardMap = new Map<string, CardData>();
  
  /** Lista de predecessores para cada card (quem aponta para mim) */
  const predecessors = new Map<string, string[]>();
  
  /** Grau de entrada (in-degree) para ordenação topológica */
  const inDegree = new Map<string, number>();
  
  // Inicializar estruturas
  activeCards.forEach(card => {
    cardMap.set(card.id, card);
    predecessors.set(card.id, []);
    inDegree.set(card.id, 0);
  });
  
  // Construir grafo de dependências
  // Para cada conexão de saída, o destino tem o origem como predecessor
  activeCards.forEach(card => {
    card.conexoes_saida?.forEach((conn: Connection) => {
      const targetId = conn.targetId;
      if (cardMap.has(targetId)) {
        // Adicionar predecessor
        const preds = predecessors.get(targetId) || [];
        if (!preds.includes(card.id)) {
          preds.push(card.id);
          predecessors.set(targetId, preds);
        }
        // Incrementar in-degree
        inDegree.set(targetId, (inDegree.get(targetId) || 0) + 1);
      }
    });
  });

  // =========================================================================
  // FASE 2: Ordenação Topológica via BFS (Algoritmo de Kahn)
  // =========================================================================
  
  /** Fila de processamento (cards prontos para processar) */
  const queue: string[] = [];
  
  /** Ordem topológica resultante */
  const topologicalOrder: string[] = [];
  
  /** Mapa de tempos calculados */
  const endTimes = new Map<string, number>();
  
  // Encontrar todos os nós com in-degree = 0 (cards iniciais)
  activeCards.forEach(card => {
    if ((inDegree.get(card.id) || 0) === 0) {
      queue.push(card.id);
      endTimes.set(card.id, 0); // Cards iniciais começam em t=0
    }
  });
  
  // Processar BFS
  while (queue.length > 0) {
    const cardId = queue.shift()!;
    topologicalOrder.push(cardId);
    
    const card = cardMap.get(cardId)!;
    
    // Para cada sucessor deste card
    card.conexoes_saida?.forEach((conn: Connection) => {
      const targetId = conn.targetId;
      if (!cardMap.has(targetId)) return;
      
      // Decrementar in-degree
      const newDegree = (inDegree.get(targetId) || 1) - 1;
      inDegree.set(targetId, newDegree);
      
      // Se todos os predecessores foram processados, adicionar à fila
      if (newDegree === 0) {
        queue.push(targetId);
      }
    });
  }

  // =========================================================================
  // FASE 3: Calcular tempos (Forward Pass)
  // =========================================================================
  
  const tasks: GanttTask[] = [];
  let sequence = 0;
  
  topologicalOrder.forEach(cardId => {
    const card = cardMap.get(cardId)!;
    const preds = predecessors.get(cardId) || [];
    
    // ----- Calcular StartTime -----
    // Regra de Convergência: usar o MAIOR endTime entre todos os predecessores
    let startMinutes = 0;
    if (preds.length > 0) {
      startMinutes = Math.max(...preds.map(predId => endTimes.get(predId) || 0));
    }
    
    // ----- Calcular Durações -----
    // TRA (Touch Time) = tempo de trabalho efetivo (sempre em minutos)
    const workDuration = Math.max(
      0,
      parseNumericValue(card.tt) || parseNumericValue((card as any).tra),
    );
    
    // TP (Lead Time) = tempo total de permanência
    // Converter para minutos se a unidade for 'dias'
    const ltValue = parseNumericValue(card.lt ?? card.tp);
    const totalDuration = convertToMinutes(ltValue, card.tpUnit);
    
    // Espera = TP - TRA (tempo que o item fica parado)
    const waitDuration = Math.max(0, totalDuration - workDuration);
    
    // EndTime = StartTime + TotalDuration
    const endMinutes = startMinutes + totalDuration;
    
    // Armazenar endTime para cálculo dos sucessores
    endTimes.set(cardId, endMinutes);
    
    // ----- Construir GanttTask -----
    const resource = extractResource(card);
    
    // Determinar se o card deve ser renderizado no Gantt
    // Cards inicial, final e decisão participam do cálculo de tempo mas não são exibidos
    const isRenderable = card.tipo !== 'card_inicial' && 
                         card.tipo !== 'card_final' && 
                         card.tipo !== 'decisao' &&
                         resource !== 'Não Atribuído';

    const task: GanttTask = {
      id: card.id,
      name: card.atividade || card.nome_fluxo || `Card ${cardId.slice(0, 6)}`,
      resource: isRenderable ? resource : '__hidden__',
      startMinutes,
      workDuration,
      waitDuration,
      totalDuration,
      endMinutes,
      dependencies: preds,
      type: mapCardType(card.tipo),
      sequence: sequence++,
      probability: card.tipo === 'decisao' 
        ? (card.decisao_sim_pct || 50) 
        : 100,
    };
    tasks.push(task);
  });

  // =========================================================================
  // FASE 4: Identificar Caminho Crítico (Backward Pass)
  // =========================================================================
  
  // O caminho crítico são os cards cujo endTime == max(endTimes) em cada nível
  const maxEndTime = Math.max(...Array.from(endTimes.values()));
  const criticalPath: string[] = [];
  
  // Backward: partir do final e voltar pelos predecessores críticos
  const endCards = tasks.filter(t => t.endMinutes === maxEndTime);
  const visited = new Set<string>();
  
  function traceCriticalPath(taskId: string) {
    if (visited.has(taskId)) return;
    visited.add(taskId);
    criticalPath.unshift(taskId); // Adicionar no início
    
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.dependencies.length === 0) return;
    
    // Encontrar o predecessor crítico (aquele cujo endTime == startTime deste)
    const criticalPred = task.dependencies.find(predId => {
      const predTask = tasks.find(t => t.id === predId);
      return predTask && predTask.endMinutes === task.startMinutes;
    });
    
    if (criticalPred) {
      traceCriticalPath(criticalPred);
    }
  }
  
  endCards.forEach(t => traceCriticalPath(t.id));

  // =========================================================================
  // FASE 5: Agrupar por Recurso
  // =========================================================================
  
  const resourceGroups = new Map<string, GanttTask[]>();
  
  tasks.forEach(task => {
    const group = resourceGroups.get(task.resource) || [];
    group.push(task);
    resourceGroups.set(task.resource, group);
  });
  
  // Ordenar tarefas dentro de cada grupo por startTime
  resourceGroups.forEach((groupTasks, resource) => {
    groupTasks.sort((a, b) => a.startMinutes - b.startMinutes);
    resourceGroups.set(resource, groupTasks);
  });

  // =========================================================================
  // FASE 5.1: Preencher Gaps com barras de espera (NVA)
  // =========================================================================
  // Para cada recurso, verificar se há gaps entre tarefas consecutivas
  // e criar barras de espera para representar o Lead Time real
  
  const tasksWithGaps: GanttTask[] = [...tasks];
  let gapCounter = 0;
  
  resourceGroups.forEach((groupTasks, resource) => {
    for (let i = 0; i < groupTasks.length - 1; i++) {
      const currentTask = groupTasks[i];
      const nextTask = groupTasks[i + 1];
      
      // Verificar se há gap entre o fim da tarefa atual e início da próxima
      const gapStart = currentTask.endMinutes;
      const gapEnd = nextTask.startMinutes;
      const gapDuration = gapEnd - gapStart;
      
      if (gapDuration > 0) {
        // Criar barra de gap/espera
        const gapTask: GanttTask = {
          id: `gap-${resource}-${gapCounter++}`,
          name: 'Espera',
          resource,
          startMinutes: gapStart,
          workDuration: 0,
          waitDuration: gapDuration,
          totalDuration: gapDuration,
          endMinutes: gapEnd,
          dependencies: [currentTask.id],
          type: 'activity',
          sequence: currentTask.sequence + 0.5,
          probability: 100,
          isGap: true
        };
        
        tasksWithGaps.push(gapTask);
      }
    }
  });
  
  // Reordenar tarefas com gaps incluídos
  tasksWithGaps.sort((a, b) => a.startMinutes - b.startMinutes || a.sequence - b.sequence);
  
  // Reconstruir resourceGroups com gaps
  const resourceGroupsWithGaps = new Map<string, GanttTask[]>();
  tasksWithGaps.forEach(task => {
    const group = resourceGroupsWithGaps.get(task.resource) || [];
    group.push(task);
    resourceGroupsWithGaps.set(task.resource, group);
  });
  
  resourceGroupsWithGaps.forEach((groupTasks, resource) => {
    groupTasks.sort((a, b) => a.startMinutes - b.startMinutes);
    resourceGroupsWithGaps.set(resource, groupTasks);
  });

  // =========================================================================
  // FASE 6: Calcular Estatísticas
  // =========================================================================
  
    const totalWorkTime = tasks.reduce((sum, t) => sum + t.workDuration, 0);
    // totalWaitTime agora é o Lead Time Total (maxEndTime) menos o total de TRA do caminho crítico
    const totalWaitTime = maxEndTime - tasks.filter(t => criticalPath.includes(t.id)).reduce((sum, t) => sum + t.workDuration, 0);
    const gapTime = tasksWithGaps.filter(t => t.isGap).reduce((sum, t) => sum + t.waitDuration, 0);
    const efficiency = totalWorkTime + totalWaitTime + gapTime > 0
      ? (totalWorkTime / (totalWorkTime + totalWaitTime + gapTime)) * 100
      : 0;

  // =========================================================================
  // FASE 7: Calcular Zoom Dinâmico
  // =========================================================================
  // Ajustar escala para que tarefas de minutos sejam legíveis em janela de ~3h (180min)
  
  let tickInterval: number;
  let tickLabel: string;
  let scale: number;
  
  if (maxEndTime <= 30) {
    // Até 30 minutos: marcas a cada 5 minutos
    tickInterval = 5;
    tickLabel = '5min';
    scale = 5; // 5 min por 100px
  } else if (maxEndTime <= 60) {
    // Até 1 hora: marcas a cada 10 minutos
    tickInterval = 10;
    tickLabel = '10min';
    scale = 10;
  } else if (maxEndTime <= 180) {
    // Até 3 horas: marcas a cada 15 minutos
    tickInterval = 15;
    tickLabel = '15min';
    scale = 15;
  } else if (maxEndTime <= 480) {
    // Até 8 horas (1 dia): marcas a cada 30 minutos
    tickInterval = 30;
    tickLabel = '30min';
    scale = 30;
  } else if (maxEndTime <= 960) {
    // Até 2 dias: marcas a cada 1 hora
    tickInterval = 60;
    tickLabel = '1h';
    scale = 60;
  } else if (maxEndTime <= 2400) {
    // Até 5 dias: marcas a cada 2 horas
    tickInterval = 120;
    tickLabel = '2h';
    scale = 120;
  } else {
    // Mais de 5 dias: marcas a cada 4 horas ou 1 dia
    tickInterval = 240;
    tickLabel = '4h';
    scale = 240;
  }

  // =========================================================================
  // RETORNO
  // =========================================================================
  
  return {
    tasks: tasksWithGaps,
    totalDuration: maxEndTime,
    resourceGroups: resourceGroupsWithGaps,
    criticalPath,
    stats: {
      totalWorkTime,
      totalWaitTime: totalWaitTime,
      efficiency,
      resourceCount: resourceGroupsWithGaps.size
    },
    zoom: {
      scale,
      tickInterval,
      tickLabel
    }
  };
}

// ============================================================================
// FUNÇÕES DE FORMATAÇÃO
// ============================================================================

/**
 * Formata minutos para exibição legível
 * Ex: 90 -> "1h 30min", 1440 -> "3d"
 */
export function formatDuration(minutes: number): string {
  if (minutes <= 0) return '0min';
  
  const days = Math.floor(minutes / 480); // 8h úteis
  const hours = Math.floor((minutes % 480) / 60);
  const mins = Math.round(minutes % 60);
  
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0 && days === 0) parts.push(`${mins}min`);
  
  return parts.join(' ') || '0min';
}

/** Linha do Gantt VSM — uma etapa do caminho crítico */
export interface CriticalPathGanttRow {
  id: string;
  stepNumber: number;
  label: string;
  name: string;
  startMinutes: number;
  workDuration: number;
  waitDuration: number;
  totalDuration: number;
  endMinutes: number;
}

export interface CriticalPathGanttResult {
  rows: CriticalPathGanttRow[];
  totalDuration: number;
  pathIds: string[];
  stats: {
    totalWorkTime: number;
    totalWaitTime: number;
    efficiency: number;
    activityCount: number;
  };
  zoom: GanttTransformResult['zoom'];
}

function isActivityCard(card: CardData): boolean {
  return card.tipo === 'atividade' && card.status !== 'removido' && card.status !== 'deletado';
}

function resolveCriticalPathIds(cards: CardData[], criticalPathIds?: string[]): string[] {
  if (criticalPathIds && criticalPathIds.length > 0) return criticalPathIds;
  return transformToGantt(cards).criticalPath;
}

function computeZoom(totalMinutes: number): GanttTransformResult['zoom'] {
  if (totalMinutes <= 30) return { scale: 5, tickInterval: 5, tickLabel: '5min' };
  if (totalMinutes <= 60) return { scale: 10, tickInterval: 10, tickLabel: '10min' };
  if (totalMinutes <= 180) return { scale: 15, tickInterval: 15, tickLabel: '15min' };
  if (totalMinutes <= 480) return { scale: 30, tickInterval: 30, tickLabel: '30min' };
  if (totalMinutes <= 960) return { scale: 60, tickInterval: 60, tickLabel: '1h' };
  if (totalMinutes <= 2400) return { scale: 120, tickInterval: 120, tickLabel: '2h' };
  return { scale: 240, tickInterval: 240, tickLabel: '4h' };
}

/**
 * Monta o Gantt estritamente por etapas do caminho crítico (Lean VSM).
 * Start de cada barra = soma acumulada de TP das etapas anteriores na cadeia.
 */
export function buildCriticalPathGantt(
  cards: CardData[],
  criticalPathIds?: string[],
): CriticalPathGanttResult {
  const activeCards = cards.filter(c => c.status !== 'removido' && c.status !== 'deletado');
  const cardMap = new Map(activeCards.map(c => [c.id, c]));
  const pathIds = resolveCriticalPathIds(activeCards, criticalPathIds);

  const empty: CriticalPathGanttResult = {
    rows: [],
    totalDuration: 0,
    pathIds: [],
    stats: { totalWorkTime: 0, totalWaitTime: 0, efficiency: 0, activityCount: 0 },
    zoom: computeZoom(0),
  };

  if (pathIds.length === 0) return empty;

  const startById = new Map<string, number>();
  let cumulativeTp = 0;

  for (const id of pathIds) {
    startById.set(id, cumulativeTp);
    const card = cardMap.get(id);
    if (!card || card.tipo === 'card_inicial') continue;
    if (card.tipo === 'decisao') continue;
    cumulativeTp += convertLtToMinutes(parseNumericValue(card.lt ?? card.tp), card.tpUnit);
  }

  const activityIds = pathIds.filter(id => {
    const card = cardMap.get(id);
    return card && isActivityCard(card);
  });

  const rows: CriticalPathGanttRow[] = activityIds.map((id, index) => {
    const card = cardMap.get(id)!;
    const workDuration = Math.max(
      0,
      parseNumericValue(card.tt) || parseNumericValue((card as { tra?: unknown }).tra),
    );
    const totalDuration = convertLtToMinutes(parseNumericValue(card.lt ?? card.tp), card.tpUnit);
    const waitDuration = Math.max(0, totalDuration - workDuration);
    const startMinutes = startById.get(id) ?? 0;
    const name = card.atividade || card.nome_fluxo || `Etapa ${index + 1}`;

    return {
      id,
      stepNumber: index + 1,
      label: `${index + 1}. ${name}`,
      name,
      startMinutes,
      workDuration,
      waitDuration,
      totalDuration,
      endMinutes: startMinutes + totalDuration,
    };
  });

  const totalDuration = rows.length > 0 ? rows[rows.length - 1].endMinutes : 0;
  const totalWorkTime = rows.reduce((sum, row) => sum + row.workDuration, 0);
  const totalWaitTime = rows.reduce((sum, row) => sum + row.waitDuration, 0);
  const efficiency = totalDuration > 0 ? (totalWorkTime / totalDuration) * 100 : 0;

  return {
    rows,
    totalDuration,
    pathIds: activityIds,
    stats: {
      totalWorkTime,
      totalWaitTime,
      efficiency,
      activityCount: rows.length,
    },
    zoom: computeZoom(totalDuration),
  };
}

/**
 * Converte estrutura para formato compatível com bibliotecas de Gantt
 * (ex: react-gantt-timeline, frappe-gantt)
 */
export function toGanttLibraryFormat(result: GanttTransformResult): Array<{
  id: string;
  name: string;
  start: Date;
  end: Date;
  progress: number;
  dependencies: string;
  custom_class: string;
}> {
  const baseDate = new Date();
  baseDate.setHours(8, 0, 0, 0); // 8:00 AM
  
  return result.tasks.map(task => {
    const start = new Date(baseDate.getTime() + task.startMinutes * 60000);
    const end = new Date(baseDate.getTime() + task.endMinutes * 60000);
    const progress = task.totalDuration > 0 
      ? (task.workDuration / task.totalDuration) * 100 
      : 0;
    
    return {
      id: task.id,
      name: task.name,
      start,
      end,
      progress,
      dependencies: task.dependencies.join(', '),
      custom_class: result.criticalPath.includes(task.id) ? 'critical' : task.type
    };
  });
}
